import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
	Users,
	FileText,
	DollarSign,
	Calendar,
	MapPin,
	Clock,
	Briefcase,
	TrendingUp,
	TrendingDown,
	Handshake,
	Bell,
	CheckCircle2,
	MessageSquare,
	Filter,
	ChevronRight,
	MoreHorizontal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { employerService } from '../../services/employerService';

// Notifications stay as inline data (no backend for this yet)
const staticNotifications = [
	{ id: 1, type: 'application', title: 'New Application', desc: 'Check your applications tab', time: 'Recently', color: 'blue', icon: Users },
	{ id: 2, type: 'completion', title: 'Job Completed', desc: 'Check completed jobs', time: 'Recently', color: 'green', icon: CheckCircle2 },
	{ id: 3, type: 'message', title: 'New Message', desc: 'Check your messages', time: 'Recently', color: 'purple', icon: MessageSquare },
];

const EmployerDashboard = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [stats, setStats] = useState([]);
	const [recentJobs, setRecentJobs] = useState([]);
	const [recentApps, setRecentApps] = useState([]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				// Fetch stats
				const statsRes = await employerService.getEmployerStats();
				const s = statsRes.data || {};
				setStats([
					{ id: 'postedJobs', titleKey: 'employerDashboard.stats.postedJobs', value: String(s.total_jobs || 0), changeKey: 'employerDashboard.stats.postedJobsChange', icon: Briefcase, color: 'blue', changeColor: 'text-green-500' },
					{ id: 'applications', titleKey: 'employerDashboard.stats.applications', value: String(s.total_applications || 0), changeKey: 'employerDashboard.stats.applicationsChange', icon: Users, color: 'green', changeColor: 'text-green-500' },
					{ id: 'moneySpent', titleKey: 'employerDashboard.stats.moneySpent', value: String(s.total_spent || 0), changeKey: 'employerDashboard.stats.moneySpentChange', icon: DollarSign, color: 'orange', changeColor: 'text-orange-500', isDown: true },
					{ id: 'hired', titleKey: 'employerDashboard.stats.hired', value: String(s.total_hired || 0), changeKey: 'employerDashboard.stats.hiredChange', icon: Handshake, color: 'purple', changeColor: 'text-green-500' },
				]);
			} catch (err) {
				console.error('Stats fetch failed:', err);
				// fallback empty stats
				setStats([
					{ id: 'postedJobs', titleKey: 'employerDashboard.stats.postedJobs', value: '0', changeKey: 'employerDashboard.stats.postedJobsChange', icon: Briefcase, color: 'blue', changeColor: 'text-green-500' },
					{ id: 'applications', titleKey: 'employerDashboard.stats.applications', value: '0', changeKey: 'employerDashboard.stats.applicationsChange', icon: Users, color: 'green', changeColor: 'text-green-500' },
					{ id: 'moneySpent', titleKey: 'employerDashboard.stats.moneySpent', value: '0', changeKey: 'employerDashboard.stats.moneySpentChange', icon: DollarSign, color: 'orange', changeColor: 'text-orange-500', isDown: true },
					{ id: 'hired', titleKey: 'employerDashboard.stats.hired', value: '0', changeKey: 'employerDashboard.stats.hiredChange', icon: Handshake, color: 'purple', changeColor: 'text-green-500' },
				]);
			}

			try {
				// Fetch recent jobs
				const jobsRes = await employerService.getMyJobs();
				const jobs = (jobsRes.data || []).slice(0, 3).map(j => ({
					id: j.id,
					title: j.title,
					description: j.description || '',
					location: j.city || '',
					date: j.created_at ? getTimeAgo(j.created_at) : '',
					type: j.payment_type || 'fixed',
					price: `Rs. ${Number(j.budget || 0).toLocaleString()}`,
				}));
				setRecentJobs(jobs);
			} catch (err) {
				console.error('Recent jobs fetch failed:', err);
			}
		};
		fetchData();
	}, []);

	function getTimeAgo(dateStr) {
		const diff = Date.now() - new Date(dateStr).getTime();
		const mins = Math.floor(diff / 60000);
		if (mins < 60) return `${mins}m ago`;
		const hrs = Math.floor(mins / 60);
		if (hrs < 24) return `${hrs}h ago`;
		return `${Math.floor(hrs / 24)}d ago`;
	}

	return (
		<div className="max-w-7xl mx-auto space-y-6 pb-12">

			{/* 1. Stats Grid */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
				{stats.map((stat) => (
					<div key={stat.id} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between group">
						<div className="space-y-1">
							<p className="text-gray-500 text-sm font-medium">{t(stat.titleKey)}</p>
							<h3 className="text-3xl font-extrabold text-gray-900">{stat.value}</h3>
							<div className={`flex items-center gap-1 text-xs font-bold ${stat.changeColor}`}>
								{stat.isDown ? <TrendingUp size={12} className="rotate-180" /> : <TrendingUp size={12} />}
								{t(stat.changeKey)}
							</div>
						</div>
						<div className={`p-4 rounded-xl flex items-center justify-center transition-all group-hover:scale-110 ${stat.color === 'blue' ? 'bg-blue-50 text-blue-500' :
							stat.color === 'green' ? 'bg-green-50 text-green-500' :
								stat.color === 'orange' ? 'bg-orange-50 text-orange-500' :
									'bg-purple-50 text-purple-500'
							}`}>
							<stat.icon size={28} />
						</div>
					</div>
				))}
			</div>

			{/* 2. Middle Row: Recently Posted & Notifications */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Recently Posted */}
				<div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
					<div className="flex items-center justify-between mb-6">
						<h3 className="text-xl font-bold text-gray-900">{t('employerDashboard.recentlyPosted')}</h3>
						<button onClick={() => navigate('/employer/jobs')} className="text-blue-600 text-sm font-bold hover:underline">{t('employerDashboard.viewAll')}</button>
					</div>

					<div className="space-y-4">
						{recentJobs.map(job => (
							<div key={job.id} className="p-5 rounded-2xl border border-gray-100 hover:border-blue-100 transition-all group">
								<div className="flex justify-between items-start">
									<div className="space-y-2 flex-1">
										<h4 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{job.title}</h4>
										<p className="text-gray-500 text-sm leading-relaxed line-clamp-2 md:max-w-xl">{job.description}</p>
										<div className="flex flex-wrap items-center gap-4 pt-2">
											<span className="flex items-center gap-1.5 text-xs font-bold text-gray-400">
												<MapPin size={14} /> {job.location}
											</span>
											<span className="flex items-center gap-1.5 text-xs font-bold text-gray-400">
												<Calendar size={14} /> {job.date}
											</span>
											<span className="flex items-center gap-1.5 text-xs font-bold text-gray-400">
												<Clock size={14} /> {job.type}
											</span>
										</div>
									</div>
									<div className="text-right whitespace-nowrap pl-4">
										<span className="text-blue-600 font-extrabold text-lg">{job.price}</span>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Recent Notifications */}
				<div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
					<div className="flex items-center justify-between mb-6">
						<h3 className="text-xl font-bold text-gray-900">{t('employerDashboard.recentNotifications')}</h3>
						<button onClick={() => navigate('/Notifications')} className="text-blue-600 text-sm font-bold hover:underline">{t('employerDashboard.viewAll')}</button>
					</div>

					<div className="space-y-4">
						{staticNotifications.map(notif => (
							<div key={notif.id} className={`p-4 rounded-2xl flex items-start gap-4 ${notif.color === 'blue' ? 'bg-blue-50/50' :
								notif.color === 'green' ? 'bg-green-50/50' :
									'bg-purple-50/50'
								}`}>
								<div className={`p-2 rounded-full flex-shrink-0 ${notif.color === 'blue' ? 'bg-blue-500 text-white' :
									notif.color === 'green' ? 'bg-green-500 text-white' :
										'bg-purple-500 text-white'
									}`}>
									<notif.icon size={16} />
								</div>
								<div className="space-y-0.5 min-w-0">
									<h4 className="font-bold text-gray-900 text-sm truncate">{notif.title}</h4>
									<p className="text-gray-600 text-xs truncate">{notif.desc}</p>
									<p className="text-blue-500 text-[10px] font-bold mt-1 uppercase tracking-wider">{notif.time}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* 3. Bottom Table: Recent Applications */}
			<div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-6">
				<div className="flex items-center justify-between mb-8">
					<h3 className="text-xl font-bold text-gray-900">{t('employerDashboard.recentApplications')}</h3>
					<div className="flex items-center gap-4">
						<Button variant="outline" className="flex items-center gap-2 font-bold text-gray-500" onClick={() => navigate('/employer/applications')}>
							<Filter size={16} /> {t('employerDashboard.filter')}
						</Button>
						<button onClick={() => navigate('/employer/applications')} className="text-blue-600 text-sm font-bold hover:underline">{t('employerDashboard.viewAllApplications')}</button>
					</div>
				</div>

				<div className="overflow-x-auto">
					<table className="w-full text-left">
						<thead>
							<tr className="border-b border-gray-50">
								<th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-widest pl-2">{t('employerDashboard.table.applicant')}</th>
								<th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-widest">{t('employerDashboard.table.jobPosting')}</th>
								<th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-widest">{t('employerDashboard.table.experience')}</th>
								<th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-widest">{t('employerDashboard.table.appliedDate')}</th>
								<th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-widest">{t('employerDashboard.table.status')}</th>
								<th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-widest pr-2 text-right">{t('employerDashboard.table.actions')}</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-50">
							{recentApps.map((app) => (
								<tr key={app.id} className="group hover:bg-slate-50/50 transition-colors">
									<td className="py-5 pl-2">
										<div className="flex items-center gap-3">
											<img src={app.avatar} alt={app.name} className="w-10 h-10 rounded-full border border-gray-100 bg-slate-50" />
											<div>
												<p className="text-sm font-bold text-gray-900">{app.name}</p>
												<p className="text-[11px] font-medium text-gray-400">{app.email}</p>
											</div>
										</div>
									</td>
									<td className="py-5">
										<p className="text-sm font-bold text-gray-600">{app.job}</p>
									</td>
									<td className="py-5">
										<p className="text-sm font-bold text-gray-600">{app.experience}</p>
									</td>
									<td className="py-5">
										<p className="text-sm font-bold text-gray-600">{app.date}</p>
									</td>
									<td className="py-5">
										<Badge variant="outline" className="bg-orange-50 text-orange-600 border-none font-bold py-1 px-3">
											{app.status}
										</Badge>
									</td>
									<td className="py-5 text-right pr-2">
										<button className="p-2 text-gray-300 hover:text-gray-600 transition-colors">
											<MoreHorizontal size={20} />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default EmployerDashboard;
